import type { EventProps } from "./events";
import { parseBookingTime } from "@/lib/booking-time";

type RentalType = "driving" | "chauffeured" | "event";

function clockMinutes(value: string): number | null {
  const parsed = parseBookingTime(value);
  return parsed ? parsed.hour24 * 60 + parsed.minute : null;
}

function selectedTimestamp(date: string, clock: string): string | null {
  const minutes = clockMinutes(clock);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || minutes === null) return null;
  const hour = Math.floor(minutes / 60).toString().padStart(2, "0");
  const minute = (minutes % 60).toString().padStart(2, "0");
  return `${date}T${hour}:${minute}:00`;
}

export function buildDateRangeRejectionProps(input: {
  checkoutJourneyId: string;
  carId: number;
  rentalType: RentalType;
  dateFrom: string;
  dateTo: string;
  pickupTime: string;
  dropoffTime: string;
  drivingMinimumDays?: number | null;
}): EventProps<"date_range_rejected"> {
  const start = selectedTimestamp(input.dateFrom, input.pickupTime);
  const end = selectedTimestamp(input.dateTo, input.dropoffTime);
  const startMs = start ? Date.parse(start) : NaN;
  const endMs = end ? Date.parse(end) : NaN;
  const rawDurationHours =
    Number.isFinite(startMs) && Number.isFinite(endMs)
      ? Math.round(((endMs - startMs) / 3_600_000) * 100) / 100
      : null;
  const sameDayClockMinutes =
    clockMinutes(input.dropoffTime) != null && clockMinutes(input.pickupTime) != null
      ? clockMinutes(input.dropoffTime)! - clockMinutes(input.pickupTime)!
      : null;

  let rule: EventProps<"date_range_rejected">["rule"] = "invalid_date_format";
  let durationHours = rawDurationHours;
  if (start && end) {
    if (input.rentalType === "chauffeured") {
      durationHours = sameDayClockMinutes == null
        ? null
        : Math.round((sameDayClockMinutes / 60) * 100) / 100;
      rule = durationHours != null && durationHours > 24
        ? "chauffeured_max_24_hours"
        : "dropoff_after_pickup";
    } else {
      rule = "dropoff_after_pickup";
    }
  }

  return {
    checkout_journey_id: input.checkoutJourneyId,
    car_id: input.carId,
    rental_type: input.rentalType,
    rule,
    selected_start: start,
    selected_end: end,
    duration_hours: durationHours,
    minimum_duration_hours:
      input.rentalType === "driving"
        ? Math.max(1, input.drivingMinimumDays ?? 1) * 24
        : 1,
  };
}

export function checkoutHandoffContext(next: string): {
  checkoutJourneyId: string;
  carId?: number;
} | null {
  try {
    const url = new URL(next, "https://checkout.invalid");
    if (!url.pathname.endsWith("/checkout")) return null;
    const checkoutJourneyId = url.searchParams.get("h")?.toLowerCase() ?? "";
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(checkoutJourneyId)) {
      return null;
    }
    const carId = Number(url.searchParams.get("car"));
    return {
      checkoutJourneyId,
      carId: Number.isSafeInteger(carId) && carId > 0 ? carId : undefined,
    };
  } catch {
    return null;
  }
}

export function isCheckoutTimeRangeRejected(input: {
  rentalType: RentalType;
  dateFrom: string;
  dateTo: string;
  pickupTime: string;
  dropoffTime: string;
}): boolean {
  const pickup = clockMinutes(input.pickupTime);
  const dropoff = clockMinutes(input.dropoffTime);
  if (pickup == null || dropoff == null || !input.dateFrom || !input.dateTo) return false;
  const span = dropoff - pickup;
  if (input.rentalType === "chauffeured") return span <= 0 || span > 24 * 60;
  return input.dateFrom === input.dateTo && span <= 0;
}